import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import OnboardingShell from '@/components/OnboardingShell';
import {
  ActiveHoursDaysStep,
  ReminderFrequencyStep,
  DailyGoalStep,
} from '@/components/onboarding/SharedSteps';
import { ImageUpload } from '@/components/image-upload';
import SwygWaveMark from '@/components/SwygWaveMark';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  calculateBaseDailyRecommendation,
  calculateCompleteHydrationMetrics,
} from '@/lib/goalCalculations';
import { COUNTRY_CODES, formatPhoneE164, isValidE164 } from '@shared/phoneUtils';
import { profileCreationSchema } from '@shared/schema';
import { formatApiErrorBody } from '@/lib/errors';
import { pickPublicName, getDisplayInitials } from '@/lib/displayName';
import { normalizeAccessCode } from '@shared/accessCode';

// Step indices
const INTRO_END = 2;
const ACCOUNT_NAME_EMAIL = 3;
const ACCOUNT_EMAIL_OTP = 4;
const ACCOUNT_PHONE = 5;
const ACCOUNT_PHONE_OTP = 6;
const ACCOUNT_PASSWORD = 7;
const PROFILE_START = 8;
const PROFILE_DETAILS = 8;
const PROFILE_HABITS = 9;
const PROFILE_HOURS = 10;
const PROFILE_GOAL = 11;
const TOTAL = 12;

const ONBOARDING_STEP_KEY = 'onboarding-step';

const ACCOUNT_STEPS = [
  ACCOUNT_NAME_EMAIL,
  ACCOUNT_EMAIL_OTP,
  ACCOUNT_PHONE,
  ACCOUNT_PHONE_OTP,
  ACCOUNT_PASSWORD,
];

const DAY_MAP: Record<string, string> = {
  Mon: 'monday',
  Tue: 'tuesday',
  Wed: 'wednesday',
  Thu: 'thursday',
  Fri: 'friday',
  Sat: 'saturday',
  Sun: 'sunday',
};

type EmailType = 'business' | 'personal';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const passwordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

const WavyShape = ({ size = 100 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow">
    <path
      d="M50 2 C58 2 62 8 70 10 C78 12 86 8 90 16 C94 24 88 30 90 38 C92 46 98 52 96 60 C94 68 86 70 82 78 C78 86 80 94 72 96 C64 98 58 92 50 92 C42 92 36 98 28 96 C20 94 22 86 18 78 C14 70 6 68 4 60 C2 52 8 46 10 38 C12 30 6 24 10 16 C14 8 22 12 30 10 C38 8 42 2 50 2 Z"
      fill="hsl(var(--lime-500))"
      stroke="hsl(var(--deep-800))"
      strokeWidth="2"
    />
  </svg>
);

const Onboarding = () => {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(() => {
    try {
      const saved = sessionStorage.getItem(ONBOARDING_STEP_KEY);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!Number.isNaN(parsed) && parsed >= 0 && parsed < TOTAL) return parsed;
      }
    } catch {
      // Private mode / blocked storage must not crash signup
    }
    return 0;
  });
  const [saving, setSaving] = useState(false);

  // Account creation state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailType, setEmailType] = useState<EmailType>('business');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpTimer, setEmailOtpTimer] = useState(0);
  const [companyCode, setCompanyCode] = useState('');
  const [companyId, setCompanyId] = useState<string>();
  const [companyCodeVerified, setCompanyCodeVerified] = useState(false);
  const [companyCodeError, setCompanyCodeError] = useState('');
  const [companyCodeValidating, setCompanyCodeValidating] = useState(false);
  const [domainWhitelisted, setDomainWhitelisted] = useState(false);
  const [domainMessage, setDomainMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // Phone state
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);

  // Profile state
  const [nickname, setNickname] = useState('');
  const [avatarName, setAvatarName] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [dailyIntake, setDailyIntake] = useState('1500');
  const [drinkFrequency, setDrinkFrequency] = useState(180);
  const [activeDays, setActiveDays] = useState<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('21:00');
  const [reminderInterval, setReminderInterval] = useState(45);
  const [dailyTarget, setDailyTarget] = useState('');
  const [achievement] = useState(true);
  const [sound] = useState(true);
  const otpAutoSubmitRef = useRef(false);

  const isRegistered = !!isAuthenticated && !!user;
  const skipAccountSteps = isRegistered;

  const requiresCompanyCode = emailType === 'personal' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const goToStep = (nextStep: number) => {
    setStep(nextStep);
    try {
      sessionStorage.setItem(ONBOARDING_STEP_KEY, String(nextStep));
    } catch {
      // ignore storage failures in private mode
    }
  };

  useEffect(() => {
    if (skipAccountSteps && step < PROFILE_START) {
      goToStep(PROFILE_START);
    }
  }, [skipAccountSteps, step]);

  useEffect(() => {
    setCompanyCodeVerified(false);
    setCompanyCode('');
    if (emailType === 'personal') {
      setDomainWhitelisted(false);
      setDomainMessage('');
    }
  }, [email, emailType]);

  useEffect(() => {
    if (!requiresCompanyCode) {
      setCompanyCodeError('');
      return;
    }
    const normalized = normalizeAccessCode(companyCode);
    if (normalized.length < 3) {
      setCompanyCodeError('');
      setCompanyCodeVerified(false);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setCompanyCodeValidating(true);
      try {
        const res = await fetch('/api/auth/validate-company-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ companyCode: normalized }),
          signal: controller.signal,
        });
        const result = await res.json();
        const isCodeValid = result.valid ?? result.isValid;
        if (!res.ok || !isCodeValid) {
          setCompanyCodeError('Invalid');
          setCompanyCodeVerified(false);
        } else {
          setCompanyCodeError('');
          setCompanyId(result.company?.id);
          setCompanyCodeVerified(true);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setCompanyCodeError('Invalid');
        setCompanyCodeVerified(false);
      } finally {
        if (!controller.signal.aborted) setCompanyCodeValidating(false);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [companyCode, requiresCompanyCode]);

  useEffect(() => {
    if (!user) return;
    const u = user as Record<string, unknown>;
    if (u.firstName) setFirstName(String(u.firstName));
    if (u.lastName) setLastName(String(u.lastName));
    if (u.phoneNumber) {
      const stored = String(u.phoneNumber);
      setPhone(stored);
      const match = COUNTRY_CODES.find((c) => stored.startsWith(c.dial));
      if (match) {
        setCountryCode(match.dial);
        setPhoneLocal(stored.slice(match.dial.length));
      }
    }
    if (u.isPhoneVerified) setPhoneVerified(true);
  }, [user]);

  const recommended = () => {
    const base = calculateBaseDailyRecommendation(
      parseInt(age, 10) || undefined,
      gender || undefined,
      'moderate'
    );
    const start = parseInt(startTime.split(':')[0], 10);
    const end = parseInt(endTime.split(':')[0], 10);
    const hours = Math.max(end - start, 1);
    return Math.round((base / 16) * hours);
  };

  useEffect(() => {
    if (step === PROFILE_GOAL) {
      setDailyTarget(String(recommended()));
    }
  }, [step, startTime, endTime, gender, age]);

  useEffect(() => {
    if (emailOtpTimer <= 0) return;
    const t = setTimeout(() => setEmailOtpTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [emailOtpTimer]);

  useEffect(() => {
    if (phoneOtpTimer <= 0) return;
    const t = setTimeout(() => setPhoneOtpTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneOtpTimer]);

  const validateEmailDomain = useCallback(async (addr: string, type: EmailType) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addr)) {
      setDomainWhitelisted(false);
      setDomainMessage(addr.length > 0 ? 'Please enter a valid email address' : '');
      return;
    }

    // Personal emails use the company-code path, not domain whitelist messaging.
    if (type === 'personal') {
      setDomainWhitelisted(false);
      setDomainMessage('');
      return;
    }

    try {
      const res = await fetch(`/api/auth/validate-email-domain?email=${encodeURIComponent(addr)}`);
      const result = await res.json();
      setDomainWhitelisted(result.isWhitelisted);
      setDomainMessage(result.message || '');
    } catch {
      setDomainMessage('Unable to validate email domain.');
    }
  }, []);

  useEffect(() => {
    if (step !== ACCOUNT_NAME_EMAIL || !email) return;
    const timer = setTimeout(() => validateEmailDomain(email, emailType), 500);
    return () => clearTimeout(timer);
  }, [email, emailType, step, validateEmailDomain]);

  const formatOtpTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getFullPhone = () => formatPhoneE164(countryCode, phoneLocal);

  const isAccountStep = (s: number) => ACCOUNT_STEPS.includes(s);

  const advanceStep = (from: number) => {
    let next = from + 1;
    if (skipAccountSteps && isAccountStep(next)) {
      next = PROFILE_START;
    }
    return Math.min(next, TOTAL - 1);
  };

  const retreatStep = (from: number) => {
    let prev = from - 1;
    if (skipAccountSteps && isAccountStep(prev)) {
      prev = INTRO_END;
    }
    return Math.max(prev, 0);
  };

  const sendEmailOtp = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    const validation = emailSchema.safeParse({ email: normalizedEmail });
    if (!validation.success) {
      toast({
        variant: 'destructive',
        title: 'Invalid email',
        description: validation.error.errors[0]?.message,
      });
      return false;
    }
    setEmail(normalizedEmail);
    const res = await fetch('/api/auth/send-email-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalizedEmail }),
    });
    const result = await res.json();
    if (!res.ok) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: result.message || 'Failed to send verification code',
      });
      return false;
    }
    setEmailOtpTimer(300);
    setEmailOtp('');
    otpAutoSubmitRef.current = false;
    toast({ title: 'Code sent', description: `Check ${normalizedEmail} for your 6-digit code.` });
    return true;
  };

  const verifyEmailOtp = async (codeOverride?: string) => {
    const code = (codeOverride ?? emailOtp).replace(/\D/g, '');
    if (code.length !== 6) {
      toast({
        variant: 'destructive',
        title: 'Invalid code',
        description: 'Enter the 6-digit verification code.',
      });
      return false;
    }
    const normalizedEmail = email.trim().toLowerCase();
    const res = await fetch('/api/auth/verify-email-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalizedEmail, otp: code }),
    });
    const result = await res.json();
    if (!res.ok) {
      otpAutoSubmitRef.current = false;
      toast({
        variant: 'destructive',
        title: 'Verification failed',
        description: result.message || 'Invalid code',
      });
      return false;
    }
    setEmailVerified(true);
    toast({ title: 'Email verified' });
    return true;
  };

  const verifyCompanyCode = async () => {
    const normalized = normalizeAccessCode(companyCode);
    if (normalized.length < 3) {
      toast({
        variant: 'destructive',
        title: 'Invalid code',
        description: 'Enter your company access code (e.g. TESTONBOARD2026).',
      });
      return false;
    }
    const res = await fetch('/api/auth/validate-company-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCode: normalized }),
    });
    const result = await res.json();
    const isCodeValid = result.valid ?? result.isValid;
    if (!res.ok || !isCodeValid) {
      toast({
        variant: 'destructive',
        title: 'Invalid company code',
        description: 'Check the access code and try again. TESTONBOARD2026 is the test join key.',
      });
      return false;
    }
    const companyName = result.company?.name ?? result.companyName;
    setCompanyId(result.company?.id);
    setCompanyCodeVerified(true);
    toast({ title: 'Company verified', description: `Welcome to ${companyName}!` });
    return true;
  };

  const sendPhoneOtp = async () => {
    const fullPhone = getFullPhone();
    if (!phoneLocal.trim() || phoneLocal.replace(/\D/g, '').length < 6) {
      toast({
        variant: 'destructive',
        title: 'Invalid phone number',
        description: 'Select a country code and enter your number.',
      });
      return false;
    }
    const res = await fetch('/api/auth/send-phone-verification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber: fullPhone }),
    });
    const result = await res.json();
    if (!res.ok) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: result.message || 'Failed to send code',
      });
      return false;
    }
    setPhone(result.phoneNumber || fullPhone);
    setPhoneOtpSent(true);
    setPhoneOtpTimer(600);
    setPhoneOtp('');
    otpAutoSubmitRef.current = false;
    toast({ title: 'Code sent', description: `Sent to ${result.phoneNumber || fullPhone}` });
    return true;
  };

  const verifyPhoneOtp = async (codeOverride?: string) => {
    const code = (codeOverride ?? phoneOtp).replace(/\D/g, '');
    if (code.length !== 6) {
      toast({
        variant: 'destructive',
        title: 'Invalid code',
        description: 'Enter the 6-digit verification code.',
      });
      return false;
    }
    const res = await fetch('/api/auth/verify-phone', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phoneNumber: phone || getFullPhone(), code }),
    });
    const result = await res.json();
    if (!res.ok) {
      otpAutoSubmitRef.current = false;
      toast({
        variant: 'destructive',
        title: 'Verification failed',
        description: result.message || 'Invalid code',
      });
      return false;
    }
    setPhone(result.phoneNumber || phone || getFullPhone());
    setPhoneVerified(true);
    toast({ title: 'Phone verified' });
    return true;
  };

  const completeRegistration = async () => {
    if (!acceptedTerms || !acceptedPrivacy) {
      toast({
        variant: 'destructive',
        title: 'Agreement required',
        description: 'Please accept the Terms & Conditions and Privacy Policy to continue.',
      });
      return false;
    }

    const validation = passwordSchema.safeParse({ password, confirmPassword });
    if (!validation.success) {
      const errs: Record<string, string> = {};
      validation.error.errors.forEach((e) => {
        if (e.path[0]) errs[String(e.path[0])] = e.message;
      });
      setPasswordErrors(errs);
      return false;
    }
    setPasswordErrors({});

    // Mock persistence of legal acceptance (UI gate; backend fields can follow later)
    try {
      localStorage.setItem(
        'swyg-legal-acceptance',
        JSON.stringify({
          termsAccepted: true,
          privacyAccepted: true,
          acceptedAt: new Date().toISOString(),
          email: email.trim().toLowerCase(),
        })
      );
    } catch {
      // ignore storage failures in private mode
    }

    const res = await fetch('/api/auth/complete-registration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phone.trim() || getFullPhone(),
        companyId,
        termsAccepted: true,
        privacyAccepted: true,
      }),
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast({
        variant: 'destructive',
        title: 'Registration failed',
        description: result.message || 'Could not create account',
      });
      return false;
    }
    try {
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      await queryClient.refetchQueries({ queryKey: ['/api/auth/user'] });
    } catch (error) {
      console.error('Post-registration session refetch failed:', error);
    }
    return true;
  };

  const canProceed = () => {
    switch (step) {
      case ACCOUNT_NAME_EMAIL: {
        const hasBasics = !!(firstName.trim() && lastName.trim() && email.trim());
        if (!hasBasics) return false;
        if (requiresCompanyCode) return companyCodeVerified;
        return true;
      }
      case ACCOUNT_EMAIL_OTP:
        if (emailVerified) return true;
        return emailOtp.length === 6;
      case ACCOUNT_PHONE:
        return isValidE164(getFullPhone());
      case ACCOUNT_PHONE_OTP:
        if (!phoneVerified) return phoneOtp.length === 6;
        return true;
      case ACCOUNT_PASSWORD:
        return !!(password && confirmPassword && acceptedTerms && acceptedPrivacy);
      case PROFILE_DETAILS:
        return !!(nickname.trim() && gender);
      default:
        return true;
    }
  };

  const saveProfile = async () => {
    // Persist T&C / Privacy acceptance before finishing onboarding
    if (acceptedTerms && acceptedPrivacy) {
      const consentRes = await fetch('/api/auth/accept-terms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          acceptedTerms: true,
          acceptedPrivacy: true,
        }),
      });
      if (!consentRes.ok) {
        const err = await consentRes.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to record policy acceptance');
      }
    }

    // Public name: avatar tag → display name (nickname) → first name. Never surname.
    const resolvedAvatarName = pickPublicName([avatarName, nickname, firstName], lastName);
    if (!resolvedAvatarName) {
      toast({
        variant: 'destructive',
        title: 'Display name required',
        description: 'Enter a display name so we know what to call you.',
      });
      return;
    }
    const metrics = calculateCompleteHydrationMetrics({
      age: parseInt(age, 10) || undefined,
      gender,
      activeHoursStart: startTime,
      activeHoursEnd: endTime,
      reminderInterval,
    });
    const parsedAge = parseInt(age, 10);
    const resolvedAge =
      Number.isFinite(parsedAge) && parsedAge >= 13 && parsedAge <= 120 ? parsedAge : undefined;

    const parsedIntake = parseInt(dailyIntake, 10);
    const resolvedIntake =
      Number.isFinite(parsedIntake) && parsedIntake >= 500 && parsedIntake <= 5000
        ? parsedIntake
        : 1500;

    const parsedTarget = parseInt(dailyTarget, 10) || recommended();
    const resolvedTarget = Math.max(500, Math.min(10000, parsedTarget));

    const mappedDays = activeDays
      .map((d) => DAY_MAP[d] || d.toLowerCase())
      .filter((d) =>
        ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(d)
      );
    if (mappedDays.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Select active days',
        description: 'Choose at least one day you want water reminders.',
      });
      return;
    }

    const phoneNumber = (
      phone.trim() ||
      getFullPhone() ||
      String((user as Record<string, unknown> | undefined)?.phoneNumber || '')
    ).trim();
    if (phoneNumber.length < 10) {
      toast({
        variant: 'destructive',
        title: 'Phone number required',
        description: 'A verified phone number is required to create your profile.',
      });
      return;
    }

    const profilePayload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      avatarName: resolvedAvatarName,
      avatarType: profilePictureUrl ? 'custom' : 'default',
      profilePictureUrl: profilePictureUrl || undefined,
      phoneNumber,
      age: resolvedAge,
      gender: gender || undefined,
      currentDailyWaterIntake: resolvedIntake,
      currentDrinkingFrequency: drinkFrequency,
      activeDays: mappedDays,
      activeHoursStart: startTime,
      activeHoursEnd: endTime,
      reminderInterval,
      dailyTarget: resolvedTarget,
      proratedDailyTarget: metrics.proratedGoal,
      volumePerReminder: metrics.volumePerReminder,
      waterReminders: true,
      achievementNotifications: achievement,
      soundEnabled: sound,
    };

    const validation = profileCreationSchema.safeParse(profilePayload);
    if (!validation.success) {
      throw new Error(
        formatApiErrorBody({
          errors: validation.error.errors.map((error) => ({
            field: error.path.join('.'),
            message: error.message,
          })),
        })
      );
    }

    const response = await fetch('/api/profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(profilePayload),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(formatApiErrorBody(result));
    }

    try {
      localStorage.setItem('userNickname', nickname || firstName || 'User');
      sessionStorage.removeItem(ONBOARDING_STEP_KEY);
    } catch {
      // ignore storage failures in private mode
    }

    // Clear stale profile cache so the new photo appears immediately (staleTime is Infinity)
    queryClient.removeQueries({ queryKey: ['/api/profile'] });
    queryClient.removeQueries({ queryKey: ['/api/profile/completion-status'] });
    queryClient.removeQueries({ queryKey: ['/api/auth/user'] });

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/profile'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/profile/completion-status'] }),
    ]);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['/api/profile'] }),
      queryClient.refetchQueries({ queryKey: ['/api/profile/completion-status'] }),
      queryClient.refetchQueries({ queryKey: ['/api/auth/user'] }),
    ]);
    setLocation('/dashboard');
  };

  const next = async (otpCode?: string) => {
    if (saving) return;
    setSaving(true);
    try {
      if (step === ACCOUNT_NAME_EMAIL) {
        if (requiresCompanyCode && !companyCodeVerified) {
          const codeOk = await verifyCompanyCode();
          if (!codeOk) return;
        }
        const sent = await sendEmailOtp();
        if (!sent) return;
        goToStep(advanceStep(step));
        return;
      }

      if (step === ACCOUNT_EMAIL_OTP) {
        if (!emailVerified) {
          const ok = await verifyEmailOtp(otpCode);
          if (!ok) return;
        }
        goToStep(advanceStep(step));
        return;
      }

      if (step === ACCOUNT_PHONE) {
        const sent = await sendPhoneOtp();
        if (!sent) return;
        goToStep(advanceStep(step));
        return;
      }

      if (step === ACCOUNT_PHONE_OTP) {
        if (!phoneVerified) {
          const ok = await verifyPhoneOtp(otpCode);
          if (!ok) return;
        }
        goToStep(advanceStep(step));
        return;
      }

      if (step === ACCOUNT_PASSWORD && !isRegistered) {
        const ok = await completeRegistration();
        if (!ok) return;
        goToStep(PROFILE_START);
        return;
      }

      if (step < TOTAL - 1) {
        goToStep(advanceStep(step));
        return;
      }

      await saveProfile();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Profile setup failed',
        description: error instanceof Error ? error.message : 'Something went wrong',
      });
    } finally {
      setSaving(false);
      otpAutoSubmitRef.current = false;
    }
  };

  // Auto-advance as soon as a full OTP is entered (fixes "stuck after entering code")
  useEffect(() => {
    if (saving || otpAutoSubmitRef.current) return;
    if (step === ACCOUNT_EMAIL_OTP && !emailVerified && emailOtp.length === 6) {
      otpAutoSubmitRef.current = true;
      void next(emailOtp);
    }
    if (step === ACCOUNT_PHONE_OTP && !phoneVerified && phoneOtp.length === 6) {
      otpAutoSubmitRef.current = true;
      void next(phoneOtp);
    }
  }, [emailOtp, phoneOtp, step, emailVerified, phoneVerified, saving]);

  const back = () => {
    if (saving) return;
    otpAutoSubmitRef.current = false;
    if (step > 0) goToStep(retreatStep(step));
  };

  const nextLabel = (() => {
    if (saving) return 'Please wait...';
    if (step === TOTAL - 1) return 'Get Started';
    if (step === ACCOUNT_NAME_EMAIL) {
      if (requiresCompanyCode && !companyCodeVerified) return 'Verify & Send Code';
      return 'Send Code';
    }
    if (step === ACCOUNT_EMAIL_OTP && !emailVerified) return 'Verify Email';
    if (step === ACCOUNT_PHONE) return 'Send Code';
    if (step === ACCOUNT_PHONE_OTP && !phoneVerified) return 'Verify Phone';
    if (step === ACCOUNT_PASSWORD) return 'Create Account';
    return 'Next';
  })();

  return (
    <OnboardingShell
      step={step}
      totalSteps={TOTAL}
      variant="dots"
      onBack={back}
      onNext={next}
      canProceed={canProceed() && !saving}
      nextLabel={nextLabel}
      showBack={step > 0}
      fullWidthCta={step === 0}
    >
      {/* ── INTRO (0–2) ── */}
      {step === 0 && (
        <div className="flex flex-col items-center text-center pt-8">
          <SwygWaveMark size="lg" className="mx-auto" />
          <h1 className="text-4xl font-extrabold text-deep-800 mt-10 leading-tight">
            Welcome to
            <br />
            SWYG
          </h1>
          <p className="text-deep-500 mt-6 leading-relaxed px-4">
            We&apos;re here to help you build a hydration habit that actually sticks — just smart
            reminders and real progress.
          </p>
          <div className="mt-10 w-72 h-56 relative flex items-center justify-center">
            <svg
              viewBox="0 0 200 160"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
            >
              <path
                d="M40,20 C70,5 130,0 165,25 C195,50 198,100 175,135 C150,170 80,165 45,145 C10,125 5,75 15,50 C20,35 28,28 40,20 Z"
                fill="hsl(var(--lilac-400))"
              />
            </svg>
            <p className="relative italic text-deep-700 text-sm px-8 leading-relaxed">
              &quot;Let&apos;s be honest: we all know hydration matters. Now let&apos;s actually do
              something about it.&quot;
            </p>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="flex flex-col items-center text-center pt-4">
          <WavyShape size={90} />
          <h1 className="text-3xl font-extrabold text-deep-800 mt-6 leading-tight">
            The Two Golden Rules
          </h1>
          <p className="text-deep-500 mt-2 text-sm">
            Good hydration isn&apos;t rocket science. It&apos;s just two things:
          </p>
          <div className="relative w-full mt-10 h-80">
            <div className="absolute left-0 top-0 w-[70%]">
              <div className="relative px-5 pt-12 pb-6">
                <svg
                  viewBox="0 0 200 160"
                  className="absolute inset-0 w-full h-full"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M40,20 C70,5 130,0 165,25 C195,50 198,100 175,135 C150,170 80,165 45,145 C10,125 5,75 15,50 C20,35 28,28 40,20 Z"
                    fill="hsl(var(--lilac-400))"
                  />
                </svg>
                <div className="relative text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-purple-600 text-white font-bold flex items-center justify-center mb-2">
                    1
                  </div>
                  <p className="font-bold text-deep-800">Hit Your Daily Volume</p>
                  <p className="text-xs text-deep-500 mt-1">
                    Drink enough to replace what you lose. Simple math.
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute right-0 bottom-0 w-[78%]">
              <div className="relative px-5 pt-12 pb-6">
                <svg
                  viewBox="0 0 200 160"
                  className="absolute inset-0 w-full h-full"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M40,20 C70,5 130,0 165,25 C195,50 198,100 175,135 C150,170 80,165 45,145 C10,125 5,75 15,50 C20,35 28,28 40,20 Z"
                    fill="hsl(var(--lilac-400))"
                  />
                </svg>
                <div className="relative text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-purple-600 text-white font-bold flex items-center justify-center mb-2">
                    2
                  </div>
                  <p className="font-bold text-deep-800">Spread It Out</p>
                  <p className="text-xs text-deep-500 mt-1">
                    Chugging a liter in one go? Your body can&apos;t process that. Sip consistently
                    throughout the day instead.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col items-center text-center pt-4">
          <WavyShape size={90} />
          <h1 className="text-3xl font-extrabold text-deep-800 mt-6 leading-tight">
            Your New
            <br />
            Hydration Buddy
          </h1>
          <p className="text-deep-500 mt-3 text-sm px-6">
            SWYG sends you smart reminders and makes tracking ridiculously easy.
          </p>
          <p className="text-deep-700 font-semibold mt-8 mb-4">Two ways to log:</p>
          <div className="space-y-3 w-full">
            <div className="bg-white rounded-full px-5 py-4 flex items-center gap-4 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-lime-500 flex items-center justify-center font-bold text-deep-800">
                🔔
              </div>
              <div className="text-left">
                <p className="font-bold text-deep-800 text-sm">TAP THE NOTIFICATION</p>
                <p className="text-xs text-deep-500">(easiest)</p>
              </div>
            </div>
            <div className="bg-white rounded-full px-5 py-4 flex items-center gap-4 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center font-bold text-white">
                💧
              </div>
              <div className="text-left">
                <p className="font-bold text-deep-800 text-sm">OPEN THE APP</p>
                <p className="text-xs text-deep-500">(hit the logo)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ACCOUNT CREATION (3–7) ── */}
      {step === ACCOUNT_NAME_EMAIL && (
        <div className="space-y-5">
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">
            Create your account
          </h1>
          <p className="text-deep-500 text-sm">Let&apos;s start with the basics.</p>

          <Field label="First Name *" v={firstName} set={setFirstName} placeholder="Chiranya" />
          <Field label="Last Name *" v={lastName} set={setLastName} placeholder="Gupta" />

          <div>
            <p className="text-deep-700 text-sm mb-2 ml-1">Email type</p>
            <div className="flex rounded-full bg-white p-1 shadow-sm">
              {(['business', 'personal'] as EmailType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setEmailType(type)}
                  className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-all ${
                    emailType === type ? 'bg-purple-600 text-white' : 'text-deep-600'
                  }`}
                >
                  {type === 'business' ? 'Work Email' : 'Personal Email'}
                </button>
              ))}
            </div>
          </div>

          <Field
            label="Email *"
            v={email}
            set={(v) => {
              setEmail(v);
              setEmailVerified(false);
            }}
            placeholder={emailType === 'business' ? 'you@company.com' : 'you@gmail.com'}
            type="email"
          />
          {emailType === 'business' && domainMessage && (
            <p className={`text-xs ml-1 ${domainWhitelisted ? 'text-green-600' : 'text-deep-400'}`}>
              {domainMessage}
            </p>
          )}

          {requiresCompanyCode && (
            <div className="space-y-3 pt-1">
              <p className="text-deep-600 text-sm ml-1">
                Personal email detected — enter your company access code to continue.
              </p>
              <Field
                label="Company Code *"
                v={companyCode}
                set={(v) => {
                  setCompanyCode(normalizeAccessCode(v));
                  setCompanyCodeVerified(false);
                  setCompanyCodeError('');
                }}
                placeholder="TESTONBOARD2026"
                testId="input-company-code"
              />
              {companyCodeValidating &&
                normalizeAccessCode(companyCode).length >= 3 &&
                !companyCodeVerified && (
                  <p className="text-xs text-deep-400 ml-1">Checking code...</p>
                )}
              {companyCodeError && (
                <p className="text-xs text-red-500 ml-1" data-testid="company-code-error">
                  {companyCodeError}
                </p>
              )}
              {companyCodeVerified && (
                <p className="text-xs text-green-600 ml-1" data-testid="company-code-verified">
                  ✓ Company code verified
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {step === ACCOUNT_EMAIL_OTP && (
        <div className="space-y-5">
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">Verify your email</h1>
          <p className="text-deep-500 text-sm">
            Enter the 6-digit code sent to <strong>{email}</strong>. Verification advances
            automatically once all digits are entered.
          </p>

          {!emailVerified ? (
            <>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="000000"
                maxLength={6}
                value={emailOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setEmailOtp(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && emailOtp.length === 6 && !saving) {
                    e.preventDefault();
                    void next(emailOtp);
                  }
                }}
                className="w-full bg-white rounded-full px-5 py-4 text-deep-800 outline-none shadow-sm text-center tracking-[0.3em] text-xl font-bold"
              />
              {emailOtpTimer > 0 && (
                <p className="text-xs text-deep-500 text-center">
                  Code expires in {formatOtpTime(emailOtpTimer)}
                </p>
              )}
              <button
                type="button"
                disabled={saving || emailOtpTimer > 240}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await sendEmailOtp();
                  } finally {
                    setSaving(false);
                  }
                }}
                className="w-full text-deep-500 text-sm font-medium disabled:opacity-40"
              >
                Resend code
              </button>
            </>
          ) : (
            <div className="bg-lime-500/20 rounded-2xl px-5 py-3 text-center">
              <p className="text-deep-800 font-semibold text-sm">
                ✓ Email verified — continuing...
              </p>
            </div>
          )}
        </div>
      )}

      {step === ACCOUNT_PHONE && (
        <div className="space-y-5">
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">Your phone number</h1>
          <p className="text-deep-500 text-sm">
            Select your country code, then enter your number without the leading zero.
          </p>

          <div>
            <p className="text-deep-700 text-sm mb-2 ml-1">Country code</p>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="w-full bg-white rounded-full px-5 py-3.5 text-deep-800 outline-none shadow-sm appearance-none"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.dial} value={c.dial}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <Field
            label="Phone Number *"
            v={phoneLocal}
            set={(v) => setPhoneLocal(v.replace(/[^\d\s-]/g, ''))}
            placeholder="9876543210"
            type="tel"
          />

          {phoneLocal && (
            <p className="text-xs text-deep-400 ml-1">Will send to: {getFullPhone()}</p>
          )}
        </div>
      )}

      {step === ACCOUNT_PHONE_OTP && (
        <div className="space-y-5">
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">Verify your phone</h1>
          <p className="text-deep-500 text-sm">
            Enter the 6-digit code sent to <strong>{phone || getFullPhone()}</strong>. Verification
            advances automatically once all digits are entered.
          </p>

          {!phoneVerified ? (
            <>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="000000"
                maxLength={6}
                value={phoneOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPhoneOtp(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && phoneOtp.length === 6 && !saving) {
                    e.preventDefault();
                    void next(phoneOtp);
                  }
                }}
                className="w-full bg-white rounded-full px-5 py-4 text-deep-800 outline-none shadow-sm text-center tracking-[0.3em] text-xl font-bold"
              />
              {phoneOtpTimer > 0 && (
                <p className="text-xs text-deep-500 text-center">
                  Code expires in {formatOtpTime(phoneOtpTimer)}
                </p>
              )}
              <button
                type="button"
                disabled={saving || phoneOtpTimer > 540}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await sendPhoneOtp();
                  } finally {
                    setSaving(false);
                  }
                }}
                className="w-full text-deep-500 text-sm font-medium disabled:opacity-40"
              >
                Resend code
              </button>
            </>
          ) : (
            <div className="bg-lime-500/20 rounded-2xl px-5 py-3 text-center">
              <p className="text-deep-800 font-semibold text-sm">
                ✓ Phone verified — continuing...
              </p>
            </div>
          )}
        </div>
      )}

      {step === ACCOUNT_PASSWORD && (
        <div className="space-y-5">
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">Set your password</h1>
          <p className="text-deep-500 text-sm">Create a secure password for your account.</p>

          <Field
            label="Password *"
            v={password}
            set={setPassword}
            placeholder="Create a strong password"
            type="password"
          />
          <Field
            label="Confirm Password *"
            v={confirmPassword}
            set={setConfirmPassword}
            placeholder="Re-enter your password"
            type="password"
          />
          {passwordErrors.password && (
            <p className="text-xs text-red-500 ml-1">{passwordErrors.password}</p>
          )}
          {passwordErrors.confirmPassword && (
            <p className="text-xs text-red-500 ml-1">{passwordErrors.confirmPassword}</p>
          )}
          <p className="text-xs text-deep-400 ml-1">
            At least 8 characters with uppercase, lowercase, and a number.
          </p>

          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                className="mt-0.5"
                data-testid="checkbox-accept-terms"
              />
              <span className="text-sm text-deep-700 leading-snug">
                I agree to the{' '}
                <a
                  href="/legal/terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold text-purple-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms &amp; Conditions
                </a>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={acceptedPrivacy}
                onCheckedChange={(checked) => setAcceptedPrivacy(checked === true)}
                className="mt-0.5"
                data-testid="checkbox-accept-privacy"
              />
              <span className="text-sm text-deep-700 leading-snug">
                I agree to the{' '}
                <a
                  href="/legal/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold text-purple-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* ── PROFILE CREATION (8–11) ── */}
      {step === PROFILE_DETAILS && (
        <div className="space-y-5">
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">
            Let&apos;s get to know you <span className="inline-block">👋</span>
          </h1>
          <Field
            label="Display name *"
            v={nickname}
            set={setNickname}
            placeholder="Your public name"
          />
          <Field label="Avatar tag" v={avatarName} set={setAvatarName} placeholder="RKS" />
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-lilac-400 flex items-center justify-center text-purple-700 text-2xl font-bold overflow-hidden">
              {profilePictureUrl ? (
                <img src={profilePictureUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                getDisplayInitials({ avatarName, nickname, firstName, lastName })
              )}
            </div>
          </div>
          <div>
            <p className="text-deep-700 text-sm mb-2 ml-1">Profile Photo</p>
            <ImageUpload value={profilePictureUrl} onChange={setProfilePictureUrl} useDataUrl />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Age" v={age} set={setAge} placeholder="25" type="number" />
            <div>
              <p className="text-deep-700 text-sm mb-2 ml-1">Gender *</p>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="w-full bg-white rounded-full px-5 py-3.5 text-deep-800 outline-none shadow-sm appearance-none"
              >
                <option value="">Select</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <p className="text-xs text-deep-400 ml-1">Helps recommend your daily goal.</p>
        </div>
      )}

      {step === PROFILE_HABITS && (
        <div className="space-y-7">
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">Your Water Habits</h1>
          <p className="text-deep-500 text-sm">
            How you drink today — we use this to personalize your report.
          </p>
          <div>
            <p className="text-deep-800 font-bold mb-3">
              How much water do you typically drink per day?
            </p>
            <div className="flex items-center bg-white rounded-full px-6 py-4 shadow-sm">
              <input
                type="number"
                value={dailyIntake}
                onChange={(e) => setDailyIntake(e.target.value)}
                className="flex-1 bg-transparent outline-none text-deep-800 font-bold text-lg"
              />
              <span className="text-deep-400 text-sm">ml</span>
            </div>
            <p className="text-xs text-deep-400 mt-2">Optional – Default: 1500ml (min 500ml)</p>
          </div>
          <div>
            <p className="text-deep-800 font-bold mb-3">How often do you drink water?</p>
            <div className="grid grid-cols-3 gap-3">
              {[30, 60, 90, 180, 240].map((m) => {
                const a = drinkFrequency === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDrinkFrequency(m)}
                    className={`rounded-full py-3 font-bold transition-all ${
                      a
                        ? 'bg-purple-600 text-white shadow-[0_3px_0_hsl(var(--deep-800))]'
                        : 'bg-white text-deep-800 shadow-sm'
                    }`}
                  >
                    {m} min
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-deep-400 mt-2">Optional – Default: 180 min</p>
          </div>
        </div>
      )}

      {step === PROFILE_HOURS && (
        <ActiveHoursDaysStep
          startTime={startTime}
          endTime={endTime}
          setStartTime={setStartTime}
          setEndTime={setEndTime}
          activeDays={activeDays}
          toggleDay={(d) =>
            setActiveDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]))
          }
          title="Track My Water Intake"
          subtitle="Personalize your hydration rhythm to match your daily lifestyle."
          layout="intake"
        />
      )}

      {step === PROFILE_GOAL && (
        <div className="space-y-7">
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">Your Goal</h1>
          <DailyGoalStep
            recommendedGoal={recommended()}
            dailyTarget={dailyTarget}
            setDailyTarget={setDailyTarget}
            title=""
            subtitle=""
            showUseRecommended={false}
          />
          <ReminderFrequencyStep
            reminderInterval={reminderInterval}
            setReminderInterval={setReminderInterval}
            title=""
            subtitle="Remind me every"
          />
        </div>
      )}
    </OnboardingShell>
  );
};

const Field = ({
  label,
  v,
  set,
  placeholder,
  type = 'text',
  testId,
}: {
  label: string;
  v: string;
  set: (s: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) => (
  <div>
    <p className="text-deep-700 text-sm mb-2 ml-1">{label}</p>
    <input
      type={type}
      value={v}
      onChange={(e) => set(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      autoCapitalize="characters"
      autoCorrect="off"
      spellCheck={false}
      className="w-full bg-white rounded-full px-5 py-3.5 text-deep-800 outline-none shadow-sm"
    />
  </div>
);

export default Onboarding;
